import axios from 'axios'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import type { Stop } from '../constants/stopsData'
import type {
  LoadFormData,
  LoadState,
  LoadTimingData,
} from '../types/storageTypes'
import { api } from './fetch'

dayjs.extend(customParseFormat)

export interface LoadResponse {
  id: number
  number: number
  scale_tag_id: string
  material: string
  weight: string
  notes: string
  tolls?: string
  durations?: { [key: string]: string }
  location: string
  yard_departure_time: string | null
  arrival_time: string | null
  start_time: string | null
  departure_time: string | null
  unload_arrival_time: string | null
  unload_start_time: string | null
  unload_departure_time: string | null
  stops: Stop[]
}

export const getLoadsByTagAndLoad = async (
  tagId: string,
  loadNumber: string,
): Promise<LoadResponse> => {
  try {
    // Workbox will handle caching and offline access
    const response = await api.get('/load/', {
      params: { related_tag_id: tagId, number: loadNumber },
    })
    return response.data[0]
  } catch (error) {
    console.error('Failed to fetch loads:', error)
    throw error
  }
}

export const loadService = {
  async getLoadsByTag(tagId: string): Promise<LoadResponse[]> {
    try {
      // Workbox will handle caching and offline access
      const response = await api.get('/load/', {
        params: { related_tag_id: tagId },
      })
      return response.data
    } catch (error) {
      console.error('Failed to fetch loads:', error)
      throw error
    }
  },

  async getLoadsByWorkOrder(workorderId: string): Promise<LoadResponse[]> {
    try {
      // Workbox will handle caching and offline access
      const response = await api.get('/load/', {
        params: { workorder_id: workorderId },
      })
      return response.data
    } catch (error) {
      console.error('Failed to fetch loads:', error)
      throw error
    }
  },

  async saveLoad(
    tagId: string,
    loadNumber: string,
    formData: LoadFormData,
    timingData: LoadTimingData,
    yardDeparture?: LoadState,
  ): Promise<LoadResponse> {
    try {
      // First check if load exists
      let existingLoad: LoadResponse | null = null
      try {
        existingLoad = await getLoadsByTagAndLoad(tagId, loadNumber)
      } catch (error) {
        // Load doesn't exist, continue with creation
        console.log('No existing load found, will create new one')
      }

      const formatTimeStamp = (timestamp: string | null) => {
        if (!timestamp) return null
        const today = dayjs().format('YYYY-MM-DD')
        const dateTime = dayjs(`${today} ${timestamp}`, 'YYYY-MM-DD hh:mm A')

        return dateTime.toISOString()
      }

      const payload = {
        number: Number.parseInt(loadNumber || '0'),
        scale_tag_id: formData.scaleTagId || 'PENDING',
        material: formData.material || 'PENDING',
        weight: formData.weight ? Number.parseFloat(formData.weight) : 0,
        notes: formData.notes || 'No notes',
        tolls: formData.tolls ? Number.parseFloat(formData.tolls) : 0,
        yard_departure_time: formatTimeStamp(yardDeparture?.timestamp || null),
        arrival_time: formatTimeStamp(
          timingData.loadStates.loadArriving.timestamp,
        ),
        start_time: formatTimeStamp(timingData.loadStates.loadStart.timestamp),
        departure_time: formatTimeStamp(
          timingData.loadStates.loadDeparting.timestamp,
        ),
        unload_arrival_time: formatTimeStamp(
          timingData.loadStates.unloadArriving.timestamp,
        ),
        unload_start_time: formatTimeStamp(
          timingData.loadStates.unloadStart.timestamp,
        ),
        unload_departure_time: formatTimeStamp(
          timingData.loadStates.unloadDeparting.timestamp,
        ),
        stops: timingData.selectedStops || [],
      }

      console.log('Final API payload:', JSON.stringify(payload, null, 2))

      if (existingLoad?.id) {
        console.log(`Making PATCH request to /load/${existingLoad.id}/`)
        const response = await api.patch<LoadResponse>(
          `/load/${existingLoad.id}/`,
          {
            ...payload,
            related_tag: Number.parseInt(tagId),
          },
        )
        return response.data
      } else {
        console.log('Making POST request to /load/')
        const response = await api.post<LoadResponse>('/load/', {
          ...payload,
          related_tag_id: Number.parseInt(tagId),
        })
        return response.data
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('API Error Response:', error.response?.data)
        console.error('API Error Status:', error.response?.status)
      }
      console.error('Failed to save load:', error)
      throw error
    }
  },

  async updateLoadTiming(
    tagId: string,
    loadId: string,
    timingData: LoadTimingData,
  ): Promise<LoadResponse> {
    try {
      const formatTimeStamp = (timestamp: string | null) => {
        if (!timestamp) return null
        const today = dayjs().format('YYYY-MM-DD')
        const dateTime = dayjs(`${today} ${timestamp}`, 'YYYY-MM-DD hh:mm A')
        return dateTime.toISOString()
      }

      // Workbox will handle offline queueing
      const response = await api.patch(`/load/${loadId}/`, {
        arrival_time: formatTimeStamp(
          timingData.loadStates.loadArriving.timestamp,
        ),
        start_time: formatTimeStamp(timingData.loadStates.loadStart.timestamp),
        departure_time: formatTimeStamp(
          timingData.loadStates.loadDeparting.timestamp,
        ),
        unload_arrival_time: formatTimeStamp(
          timingData.loadStates.unloadArriving.timestamp,
        ),
        unload_start_time: formatTimeStamp(
          timingData.loadStates.unloadStart.timestamp,
        ),
        unload_departure_time: formatTimeStamp(
          timingData.loadStates.unloadDeparting.timestamp,
        ),
        stops: timingData.selectedStops,
      })
      return response.data
    } catch (error) {
      console.error('Failed to update load timing:', error)
      throw error
    }
  },

  async getLoadById(loadId: number): Promise<LoadResponse> {
    try {
      // Workbox will handle caching and offline access
      const response = await api.get(`/load/${loadId}/`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch load:', error)
      throw error
    }
  },
}
